import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { alpha, useTheme } from "@mui/material";
import {
  ClarificationField,
  getRequirementsMatrix,
  RequirementsMatrix,
  validateResourcesAgainstMatrix,
} from "../utils/backend-utils";

interface ClarificationFormProps {
  spec: ClarificationField[];
  onSubmit: (markdown: string, structured: Record<string, string | string[]>) => void;
  disabled?: boolean;
  submitLabel?: string;
  /** If true, show the header block with title + subtitle. */
  showHeader?: boolean;
  /** Per-field issues returned by the backend's quality gate. Keyed by the
   *  ClarificationField.key. Rendered as error outline + helperText inline. */
  fieldErrors?: Record<string, { problem: string; suggestion?: string }>;
}

/** Strip the " (please specify …)" / marketing suffixes from option labels so
 *  stored answers stay tidy when the user picks an Other option. */
function cleanOther(value: string): string {
  return value.replace(/\s*\(please\s+specify[^)]*\)/gi, "").trim();
}

/**
 * Structured clarification form. Redesign goals:
 *   1. Clear visual hierarchy — bold field labels, muted help text, prominent CTA.
 *   2. Required vs optional split — required fields up top (you must answer these),
 *      optional fields collapsed under a divider so the form doesn't feel like a
 *      wall of inputs.
 *   3. Live progress counter + bar — users see how close they are to being able
 *      to submit; prevents guess-and-hit-submit behaviour.
 *   4. Error clarity — red outline + helperText pinpoints exactly what to fix
 *      (powered by the backend's quality gate output).
 *   5. Keyboard-friendly — Tab order top→bottom, Enter doesn't submit the form
 *      by accident (unless inside the CTA).
 */
export function ClarificationForm({
  spec,
  onSubmit,
  disabled,
  submitLabel = "Build the plan",
  showHeader = true,
  fieldErrors = {},
}: ClarificationFormProps) {
  const theme = useTheme();
  const [answers, setAnswers] = useState<Record<string, string | string[]>>(() => {
    const init: Record<string, string | string[]> = {};
    spec.forEach((f) => {
      init[f.key] = f.type === "checkbox_group" ? [] : "";
    });
    return init;
  });
  const [otherText, setOtherText] = useState<Record<string, string>>({});

  // Requirements-matrix validation hints. Only loaded if any field has a key
  // that suggests resources/hardware (avoids extra round-trips for templates
  // that don't care). The matrix is template-independent — it lives at
  // /requirements-matrix and reflects whatever Tableau version the corpus was
  // built against. The bot uses this for soft validation only — never blocks
  // submission, just shows an inline warning Chip.
  const auth = useAuth();
  const [reqMatrix, setReqMatrix] = useState<RequirementsMatrix | null>(null);
  const hasResourcesField = useMemo(
    () => spec.some((f) => /resource|hardware|specs/i.test(f.key)),
    [spec],
  );
  useEffect(() => {
    if (!hasResourcesField || !auth.isAuthenticated) return;
    let cancelled = false;
    getRequirementsMatrix(auth)
      .then((m) => { if (!cancelled) setReqMatrix(m); })
      .catch((e) => { console.warn("requirements-matrix fetch failed", e); });
    return () => { cancelled = true; };
  }, [hasResourcesField, auth]);

  // Detect the OS the user has selected (if any) so we can filter the matrix.
  const detectedOs: "windows" | "linux" | "cross_platform" = useMemo(() => {
    const osField = spec.find((f) => /^os(_family)?$/i.test(f.key));
    if (!osField) return "cross_platform";
    const v = answers[osField.key];
    const s = (Array.isArray(v) ? v.join(" ") : String(v || "")).toLowerCase();
    if (/windows/.test(s)) return "windows";
    if (/linux|rhel|ubuntu|centos|amazon/.test(s)) return "linux";
    return "cross_platform";
    // Re-evaluated whenever any answer changes so the warnings stay live.
  }, [spec, answers]);

  const required = spec.filter((f) => f.required);
  const optional = spec.filter((f) => !f.required);

  const isAnswered = (f: ClarificationField): boolean => {
    const v = answers[f.key];
    if (Array.isArray(v)) return v.length > 0;
    return Boolean(v && String(v).trim());
  };

  const filledRequired = useMemo(() => required.filter(isAnswered).length, [required, answers]);
  const totalRequired = required.length;
  const progressPct = totalRequired === 0 ? 100 : Math.round((filledRequired / totalRequired) * 100);
  const canSubmit = !disabled && filledRequired === totalRequired;

  const handleChange = (key: string, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggleCheckbox = (key: string, option: string) => {
    setAnswers((prev) => {
      const curr = Array.isArray(prev[key]) ? (prev[key] as string[]) : [];
      const next = curr.includes(option) ? curr.filter((x) => x !== option) : [...curr, option];
      return { ...prev, [key]: next };
    });
  };

  const serialize = (): { markdown: string; structured: Record<string, string | string[]> } => {
    // Two-track output:
    //   `markdown`   — rendered back to the user + fed to LLM. Key markers are
    //                  INCLUDED here so the backend regex can map answers back
    //                  to fields; ChatMessages strips them before render.
    //   `structured` — future-proof dict, currently passed through for any
    //                  downstream consumer that doesn't want to parse markdown.
    const lines: string[] = ["## Clarifications", ""];
    const structured: Record<string, string | string[]> = {};
    spec.forEach((f) => {
      const raw = answers[f.key];
      const extra = otherText[f.key]?.trim();
      let rendered: string;
      if (Array.isArray(raw)) {
        rendered = raw.join(", ") || "(no selection)";
        structured[f.key] = raw;
      } else {
        rendered = String(raw || "(no answer)");
        structured[f.key] = String(raw || "");
      }
      if (extra) {
        const cleaned = cleanOther(extra);
        rendered = rendered === "(no answer)" || rendered === "(no selection)" ? cleaned : `${rendered} — ${cleaned}`;
        if (Array.isArray(raw)) structured[f.key] = [...(raw as string[]), cleaned];
        else structured[f.key] = `${raw || ""}${raw ? " — " : ""}${cleaned}`;
      }
      lines.push(`- **${f.question}** (key=${f.key})`);
      lines.push(`    ${rendered}`);
    });
    return { markdown: lines.join("\n"), structured };
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const { markdown, structured } = serialize();
    onSubmit(markdown, structured);
  };

  // Render one field. Split out so we can reuse across required/optional sections.
  const renderField = (f: ClarificationField, _idx: number) => {
    const type = f.type || "text";
    const value = answers[f.key];
    const extra = otherText[f.key] || "";
    const err = fieldErrors[f.key];
    const hasErr = Boolean(err);
    const helperText = err ? (err.suggestion ? `${err.problem} — ${err.suggestion}` : err.problem) : undefined;
    const answered = isAnswered(f);

    // The label is rendered above the input (not inside it) so error states
    // don't compress the question text and so long questions remain readable.
    // When `f.help` is present, a subtle (i) icon appears next to the question;
    // hovering it reveals a tooltip with the explanation + examples. We prefer
    // a hoverable icon over inline helper text because it keeps the form
    // compact by default — the help is pull-based, not push-based.
    const QuestionLabel = (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: hasErr ? theme.palette.error.main : theme.palette.text.primary,
            lineHeight: 1.4,
          }}
        >
          {f.question}
          {f.required && (
            <Tooltip title="Required">
              <Box component="span" sx={{ color: theme.palette.error.main, ml: 0.4 }}>*</Box>
            </Tooltip>
          )}
        </Typography>
        {f.help && (
          <Tooltip
            title={
              <Box sx={{ whiteSpace: "pre-line", fontSize: "0.75rem", lineHeight: 1.5, maxWidth: 320 }}>
                {f.help}
              </Box>
            }
            placement="top"
            arrow
            enterDelay={200}
          >
            <HelpOutlineIcon
              sx={{
                fontSize: 15,
                color: theme.palette.text.secondary,
                opacity: 0.6,
                cursor: "help",
                transition: "opacity 0.15s ease",
                "&:hover": { opacity: 1, color: theme.palette.primary.main },
              }}
            />
          </Tooltip>
        )}
        {answered && !hasErr && (
          <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main, ml: "auto" }} />
        )}
        {hasErr && (
          <ErrorOutlineIcon sx={{ fontSize: 16, color: theme.palette.error.main, ml: "auto" }} />
        )}
      </Box>
    );

    if (type === "text" || type === "textarea") {
      const isResourcesField = /resource|hardware|specs/i.test(f.key);
      const userText = typeof value === "string" ? value : "";
      const resourceWarnings =
        isResourcesField && reqMatrix && userText.length > 6
          ? validateResourcesAgainstMatrix(userText, detectedOs, reqMatrix)
          : [];
      return (
        <Box key={f.key}>
          {QuestionLabel}
          <TextField
            value={value || ""}
            onChange={(e) => handleChange(f.key, e.target.value)}
            placeholder={f.placeholder}
            multiline={type === "textarea"}
            rows={type === "textarea" ? 3 : 1}
            fullWidth
            size="small"
            disabled={disabled}
            error={hasErr}
            helperText={helperText}
          />
          {resourceWarnings.length > 0 && (
            <Stack direction="column" spacing={0.5} sx={{ mt: 0.75 }}>
              {resourceWarnings.map((w) => (
                <Chip
                  key={w.field}
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={w.message}
                  sx={{ height: "auto", py: 0.25, "& .MuiChip-label": { whiteSpace: "normal", fontSize: "0.72rem", lineHeight: 1.3, py: 0.25 } }}
                />
              ))}
            </Stack>
          )}
        </Box>
      );
    }

    if (type === "select") {
      return (
        <Box key={f.key}>
          {QuestionLabel}
          <FormControl fullWidth size="small" disabled={disabled} error={hasErr}>
            <Select
              value={typeof value === "string" ? value : ""}
              onChange={(e) => handleChange(f.key, e.target.value)}
              displayEmpty
            >
              <MenuItem value=""><em>— Select —</em></MenuItem>
              {(f.options || []).map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
            {typeof value === "string" && value.toLowerCase().includes("other") && (
              <TextField
                sx={{ mt: 1 }}
                size="small"
                label="Please specify"
                value={extra}
                onChange={(e) => setOtherText((p) => ({ ...p, [f.key]: e.target.value }))}
                disabled={disabled}
              />
            )}
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        </Box>
      );
    }

    if (type === "radio") {
      return (
        <Box key={f.key}>
          {QuestionLabel}
          <FormControl disabled={disabled} error={hasErr}>
            <RadioGroup
              value={typeof value === "string" ? value : ""}
              onChange={(e) => handleChange(f.key, e.target.value)}
              sx={{ pl: 0.5 }}
            >
              {(f.options || []).map((opt) => (
                <FormControlLabel
                  key={opt}
                  value={opt}
                  control={<Radio size="small" />}
                  label={opt}
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
                />
              ))}
            </RadioGroup>
            {typeof value === "string" && value.toLowerCase().includes("other") && (
              <TextField
                sx={{ mt: 1, maxWidth: 400 }}
                size="small"
                label="Please specify"
                value={extra}
                onChange={(e) => setOtherText((p) => ({ ...p, [f.key]: e.target.value }))}
                disabled={disabled}
              />
            )}
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        </Box>
      );
    }

    if (type === "checkbox_group") {
      const selected = Array.isArray(value) ? value : [];
      return (
        <Box key={f.key}>
          {QuestionLabel}
          <FormControl disabled={disabled} error={hasErr}>
            <FormGroup sx={{ pl: 0.5 }}>
              {(f.options || []).map((opt) => (
                <FormControlLabel
                  key={opt}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(opt)}
                      onChange={() => handleToggleCheckbox(f.key, opt)}
                    />
                  }
                  label={opt}
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.9rem" } }}
                />
              ))}
            </FormGroup>
            <TextField
              sx={{ mt: 1, maxWidth: 400 }}
              size="small"
              label="Other (optional)"
              value={extra}
              onChange={(e) => setOtherText((p) => ({ ...p, [f.key]: e.target.value }))}
              disabled={disabled}
            />
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        </Box>
      );
    }

    return (
      <Box key={f.key}>
        {QuestionLabel}
        <FormControl fullWidth size="small" disabled={disabled} error={hasErr}>
          <FormLabel sx={{ mb: 0.75 }}>{f.question}</FormLabel>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      {showHeader && (
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 2.5 },
            pb: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main, letterSpacing: 0.3 }}>
              A FEW DETAILS BEFORE I BUILD YOUR PLAN
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block" }}>
            Your answers determine which documentation I pull and which steps go into the plan.
            Be specific — "Windows" is not enough, "Windows Server 2022" is.
          </Typography>

          {/* Progress */}
          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={progressPct}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.divider, 0.4),
                "& .MuiLinearProgress-bar": {
                  bgcolor: progressPct === 100 ? theme.palette.success.main : theme.palette.primary.main,
                  borderRadius: 3,
                },
              }}
            />
            <Chip
              size="small"
              label={`${filledRequired} / ${totalRequired} required`}
              sx={{
                borderRadius: "6px",
                fontWeight: 600,
                fontSize: "0.7rem",
                bgcolor: progressPct === 100 ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.primary.main, 0.1),
                color: progressPct === 100 ? theme.palette.success.dark : theme.palette.primary.main,
              }}
            />
          </Box>
        </Box>
      )}

      {/* Required fields */}
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
        <Stack spacing={2.5}>
          {required.map(renderField)}
        </Stack>

        {optional.length > 0 && (
          <>
            <Divider sx={{ my: 3 }}>
              <Chip
                size="small"
                label="Optional — helps, not required"
                sx={{ borderRadius: "6px", fontSize: "0.7rem", color: theme.palette.text.secondary }}
              />
            </Divider>
            <Stack spacing={2.5}>
              {optional.map(renderField)}
            </Stack>
          </>
        )}
      </Box>

      {/* Footer CTA */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {canSubmit
            ? "All required answered. Ready to build."
            : `${totalRequired - filledRequired} more required field${totalRequired - filledRequired === 1 ? "" : "s"} to go.`}
        </Typography>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={<SendIcon />}
          size="large"
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            boxShadow: canSubmit ? 2 : 0,
          }}
        >
          {submitLabel}
        </Button>
      </Box>
    </Paper>
  );
}
